/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-array-index-key */
import React, { useState, useMemo, useRef } from 'react';
import { Global, css } from '@emotion/react';

// ── Types ──────────────────────────────────────────────────────────────────
type TabId = 'rules' | 'triggered' | 'profiles';
type UnitType = '%' | '#';
type StatusType = 'blocked' | 'resolved' | 'active' | 'dryrun';
type EscType = 'auto' | 'manual' | null;

type TrigRow = {
  uid: string;
  time: string;
  rule: string;
  history: string[];
  status: StatusType;
  escalation: EscType;
};
type Condition = {
  id: string;
  signal: string;
  window: string;
  operator: string;
  threshold: string;
  unit: UnitType;
  bounceReason: string;
};
type FilterItem =
  | { id: string; type: 'status'; s1: string; s2: string }
  | { id: string; type: 'cooldown'; days: string }
  | { id: string; type: 'alongside'; alRules: string[]; scope: string; days: string };
type Profile = {
  name: string;
  desc: string;
  actions: string[];
  popupKey: string;
  emailKeys: string[];
};
type Rule = {
  id: string;
  name: string;
  meta: string;
  active: boolean;
  status: string;
  trigCount: number;
};

// ── Static data ────────────────────────────────────────────────────────────
const TRIG_DATA_INIT: TrigRow[] = [
  {
    uid: 'usr_4f2a91c',
    time: 'Mar 15, 2026 · 09:14',
    rule: 'High bounce rate',
    history: ['Mar 15', 'Mar 12', 'Mar 08', 'Mar 01'],
    status: 'blocked',
    escalation: 'auto',
  },
  {
    uid: 'usr_7b3e02d',
    time: 'Mar 14, 2026 · 17:42',
    rule: 'High complaint rate',
    history: ['Mar 14', 'Mar 05'],
    status: 'resolved',
    escalation: null,
  },
  {
    uid: 'usr_1c9d83f',
    time: 'Mar 13, 2026 · 11:05',
    rule: 'High bounce rate',
    history: ['Mar 13', 'Mar 10', 'Mar 06', 'Mar 02', 'Feb 24', 'Feb 18'],
    status: 'blocked',
    escalation: 'auto',
  },
  {
    uid: 'usr_ae510b2',
    time: 'Mar 12, 2026 · 08:30',
    rule: 'Blocklist detected',
    history: ['Mar 12', 'Mar 03'],
    status: 'resolved',
    escalation: 'manual',
  },
  {
    uid: 'usr_92f6c71',
    time: 'Mar 11, 2026 · 14:22',
    rule: 'Low open rate',
    history: ['Mar 11', 'Mar 04', 'Feb 26'],
    status: 'active',
    escalation: null,
  },
  {
    uid: 'usr_63da04e',
    time: 'Mar 10, 2026 · 16:58',
    rule: 'High complaint rate',
    history: ['Mar 10', 'Mar 01', 'Feb 22'],
    status: 'resolved',
    escalation: null,
  },
  {
    uid: 'usr_b1072ac',
    time: 'Mar 09, 2026 · 10:11',
    rule: 'High bounce rate',
    history: ['Mar 09', 'Mar 06', 'Mar 03', 'Feb 28', 'Feb 22', 'Feb 15', 'Feb 08'],
    status: 'blocked',
    escalation: 'auto',
  },
  {
    uid: 'usr_5e3f19d',
    time: 'Mar 08, 2026 · 13:47',
    rule: 'Blocklist detected',
    history: ['Mar 08', 'Feb 27'],
    status: 'resolved',
    escalation: 'manual',
  },
  {
    uid: 'usr_d92b3e1',
    time: 'Mar 07, 2026 · 09:03',
    rule: 'High bounce rate',
    history: ['Mar 07', 'Mar 01'],
    status: 'active',
    escalation: null,
  },
  {
    uid: 'usr_0f71ca4',
    time: 'Mar 06, 2026 · 15:30',
    rule: 'High complaint rate',
    history: ['Mar 06'],
    status: 'blocked',
    escalation: 'auto',
  },
  {
    uid: 'usr_f3c920b',
    time: 'Mar 15, 2026 · 11:30',
    rule: 'Low open rate',
    history: ['Mar 15', 'Mar 09'],
    status: 'dryrun',
    escalation: null,
  },
  {
    uid: 'usr_2a871de',
    time: 'Mar 13, 2026 · 14:05',
    rule: 'High bounce rate',
    history: ['Mar 13', 'Mar 07', 'Feb 28'],
    status: 'dryrun',
    escalation: null,
  },
  {
    uid: 'usr_c047f1a',
    time: 'Mar 11, 2026 · 09:50',
    rule: 'High complaint rate',
    history: ['Mar 11'],
    status: 'dryrun',
    escalation: null,
  },
];

const ALL_ACTIONS = [
  { key: 'volume-cap', name: 'Volume cap', desc: 'Limit to 5k sends/day' },
  { key: 'popup', name: 'In-app popup', desc: 'Surface warning in UI' },
  { key: 'email', name: 'Automatic email', desc: 'Notify sender' },
  { key: 'pause', name: 'Pause sending', desc: 'Disable all sending' },
  { key: 'rotate-ip', name: 'Rotate IP pool', desc: 'Move to low-rep pool' },
  { key: 'kyc', name: 'Trigger KYC', desc: 'Require verification' },
  { key: 'domain', name: 'Require custom domain', desc: 'Force domain auth' },
  { key: 'cancel', name: 'Cancel active send', desc: 'Halt mid-campaign' },
  { key: 'postmaster', name: 'Google postmaster integration', desc: 'Request Postmaster setup' },
  { key: 'tns', name: 'TnS escalation', desc: 'Create review ticket' },
];

const POPUP_TYPES = [
  {
    key: 'bounce',
    label: 'Warning: high bounce rate or rate limit breach',
    heading: 'Your bounce rate is too high',
    body: "Your account is showing a high bounce rate, which may affect your ability to reach your subscribers' inboxes.",
  },
  {
    key: 'spam',
    label: 'Warning: high spam rate from external mailbox',
    heading: 'High spam rate detected',
    body: "We've detected a high spam rate from external mailbox providers. Your sending reputation is at risk.",
  },
  {
    key: 'google',
    label: 'Warning: spam rate nearing Google threshold',
    heading: "Spam rate nearing Google's threshold",
    body: "Your spam rate is approaching Google Postmaster's danger threshold for Gmail recipients.",
  },
  {
    key: 'open',
    label: 'Warning: low open rate detected',
    heading: 'Low open rate detected',
    body: 'Your open rate has dropped below healthy thresholds, which may signal deliverability issues.',
  },
  {
    key: 'domain',
    label: 'Warning: domain reputation issue',
    heading: 'Your domain reputation needs attention',
    body: "We've detected issues with your sending domain reputation. Please take action to protect your deliverability.",
  },
];

const EMAIL_ITEMS = [
  { key: 'remove', text: 'Remove or archive unengaged subscribers from your list' },
  { key: 'batches', text: 'Split your sending volume into smaller batches to warm up your domain' },
  { key: 'optin', text: 'Enable double opt-in for all new subscriber sign-ups' },
  { key: 'auth', text: 'Authenticate your domain with SPF, DKIM, and DMARC records' },
  { key: 'suppress', text: 'Review and clean your suppression and bounce list' },
  { key: 'postmaster', text: 'Connect Google Postmaster to monitor your domain reputation' },
];

const INIT_PROFILES: Record<string, Profile> = {
  p1: {
    name: 'Profile 1',
    desc: 'List quality warning',
    actions: ['popup', 'email', 'domain'],
    popupKey: 'bounce',
    emailKeys: ['remove', 'optin'],
  },
  p2: {
    name: 'Profile 2',
    desc: 'Domain reputation risk',
    actions: ['volume-cap', 'popup', 'email', 'pause', 'cancel', 'kyc', 'postmaster'],
    popupKey: 'domain',
    emailKeys: ['remove', 'optin', 'batches'],
  },
  p3: {
    name: 'Profile 3',
    desc: 'High spam rate',
    actions: ['popup', 'email', 'domain', 'pause', 'kyc', 'tns'],
    popupKey: 'spam',
    emailKeys: ['remove', 'optin'],
  },
};

const INIT_RULES: Rule[] = [
  {
    id: 'r1',
    name: 'High bounce rate',
    meta: 'Last triggered 2 days ago · 12 total triggers',
    active: true,
    status: 'Active',
    trigCount: 12,
  },
  {
    id: 'r2',
    name: 'High complaint rate',
    meta: 'Last triggered 5 days ago · 5 total triggers',
    active: true,
    status: 'Active',
    trigCount: 5,
  },
  {
    id: 'r3',
    name: 'Low open rate',
    meta: 'Never triggered · 0 total triggers',
    active: false,
    status: 'Inactive',
    trigCount: 0,
  },
  {
    id: 'r4',
    name: 'Blocklist detected',
    meta: 'Last triggered 1 week ago · 2 total triggers',
    active: true,
    status: 'Active',
    trigCount: 2,
  },
];

function genId() {
  return Math.random().toString(36).slice(2);
}

// ── Global CSS ─────────────────────────────────────────────────────────────
const globalStyles = css`
  .dops * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .dops {
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
    color: #1a1a1a;
    font-size: 13px;
  }
  .dops .app {
    background: #faf9f7;
    border: 1px solid #e8e4df;
    border-radius: 16px;
    overflow: hidden;
    min-height: 700px;
  }
  .dops .nav {
    background: #fff;
    border-bottom: 1px solid #e8e4df;
    padding: 0 28px;
    display: flex;
    align-items: center;
  }
  .dops .nav-logo {
    font-family: 'Georgia', serif;
    font-size: 16px;
    font-weight: 400;
    color: #1a1a1a;
    padding: 16px 0;
    margin-right: 32px;
    flex-shrink: 0;
  }
  .dops .nav-tabs {
    display: flex;
  }
  .dops .nav-tab {
    font-size: 13px;
    padding: 16px 20px;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    color: #9a9087;
    white-space: nowrap;
    margin-bottom: -1px;
  }
  .dops .nav-tab.active {
    color: #1a1a1a;
    border-bottom-color: #1a1a1a;
  }
  .dops .nav-tab:hover:not(.active) {
    color: #1a1a1a;
  }
  .dops .tab-count {
    font-size: 10px;
    background: #f3f0eb;
    color: #9a9087;
    border-radius: 20px;
    padding: 2px 7px;
    margin-left: 6px;
  }
  .dops .nav-tab.active .tab-count {
    background: #1a1a1a;
    color: #faf9f7;
  }
  .dops .divider {
    border: none;
    border-top: 1px solid #e8e4df;
    margin: 18px 0;
  }
  .dops .sec-label {
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9a9087;
    margin-bottom: 10px;
  }
  .dops .add-link {
    font-size: 12px;
    color: #1a1a1a;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .dops .btn {
    font-size: 12px;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    letter-spacing: 0.02em;
  }
  .dops .btn-ghost {
    background: none;
    border: 1px solid #e0dbd4;
    color: #1a1a1a;
  }
  .dops .btn-ghost:hover {
    background: #f5f3ef;
  }
  .dops .btn-outline {
    background: none;
    border: 1px solid #1a1a1a;
    color: #1a1a1a;
  }
  .dops .btn-outline:hover {
    background: #f5f3ef;
  }
  .dops .btn-solid {
    background: #1a1a1a;
    border: 1px solid #1a1a1a;
    color: #faf9f7;
  }
  .dops .btn-solid:hover {
    background: #333;
  }
  .dops .remove-btn {
    color: #c5bfb8;
    cursor: pointer;
    font-size: 16px;
    background: none;
    border: none;
    padding: 0;
    text-align: center;
    line-height: 1;
  }
  .dops .remove-btn:hover {
    color: #1a1a1a;
  }
  /* rules */
  .dops .rules-layout {
    display: flex;
    min-height: 660px;
  }
  .dops .sidebar {
    width: 240px;
    min-width: 240px;
    background: #f3f0eb;
    border-right: 1px solid #e8e4df;
    padding: 24px 0;
    transition:
      width 0.25s,
      min-width 0.25s,
      padding 0.25s;
    overflow: hidden;
  }
  .dops .sidebar.collapsed {
    width: 0;
    min-width: 0;
    padding: 0;
    border-right: none;
  }
  .dops .sb-top {
    padding: 0 20px 20px;
    border-bottom: 1px solid #e8e4df;
    margin-bottom: 8px;
  }
  .dops .sb-top h2 {
    font-size: 14px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 12px;
    font-family: 'Georgia', serif;
  }
  .dops .new-btn {
    width: 100%;
    padding: 9px;
    background: #1a1a1a;
    color: #faf9f7;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    cursor: pointer;
    letter-spacing: 0.04em;
  }
  .dops .rule-item {
    padding: 12px 20px;
    cursor: pointer;
    border-left: 2px solid transparent;
    white-space: nowrap;
  }
  .dops .rule-item.active {
    background: #fff;
    border-left-color: #1a1a1a;
  }
  .dops .rule-item:hover:not(.active) {
    background: #ede9e3;
  }
  .dops .r-name {
    font-size: 13px;
    color: #1a1a1a;
    margin-bottom: 3px;
  }
  .dops .r-meta {
    font-size: 11px;
    color: #9a9087;
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .dops .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }
  .dops .dot-on {
    background: #6b9e5e;
  }
  .dops .dot-off {
    background: #c5bfb8;
  }
  .dops .pill {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 20px;
  }
  .dops .pill-on {
    background: #e8f2e5;
    color: #4a7a3e;
  }
  .dops .pill-off {
    background: #ede9e3;
    color: #9a9087;
  }
  .dops .main {
    flex: 1;
    padding: 28px 32px;
    overflow-y: auto;
    min-width: 0;
  }
  .dops .rule-topbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 3px;
  }
  .dops .toggle-sb {
    background: none;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    width: 26px;
    height: 26px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9a9087;
    font-size: 13px;
    flex-shrink: 0;
  }
  .dops .toggle-sb:hover {
    border-color: #1a1a1a;
    color: #1a1a1a;
  }
  .dops .page-title {
    font-size: 20px;
    font-weight: 400;
    font-family: 'Georgia', serif;
    color: #1a1a1a;
  }
  .dops .page-sub {
    font-size: 12px;
    color: #9a9087;
    margin-bottom: 20px;
    padding-left: 38px;
  }
  .dops .row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .dops .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 10px;
  }
  .dops .field label {
    font-size: 11px;
    color: #9a9087;
    letter-spacing: 0.03em;
  }
  .dops .field input,
  .dops .field select,
  .dops .field textarea {
    font-size: 12px;
    padding: 8px 11px;
    border: 1px solid #e0dbd4;
    border-radius: 8px;
    background: #fff;
    color: #1a1a1a;
    width: 100%;
    outline: none;
  }
  .dops .field textarea {
    resize: none;
    min-height: 56px;
    line-height: 1.5;
  }
  .dops .logic-card {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    padding: 13px 15px;
    margin-bottom: 8px;
  }
  .dops .logic-row-top {
    display: grid;
    grid-template-columns: 1fr 90px;
    gap: 10px;
    margin-bottom: 10px;
    align-items: end;
  }
  .dops .logic-row-bottom {
    display: grid;
    grid-template-columns: 80px 1fr 22px;
    gap: 8px;
    align-items: center;
  }
  .dops .logic-lbl {
    font-size: 10px;
    color: #9a9087;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 3px;
  }
  .dops .logic-select,
  .dops .logic-input {
    font-size: 12px;
    padding: 6px 9px;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    background: #faf9f7;
    color: #1a1a1a;
    width: 100%;
    outline: none;
  }
  .dops .val-wrap {
    display: flex;
  }
  .dops .val-wrap .logic-input {
    border-radius: 7px 0 0 7px;
    border-right: none;
    flex: 1;
    min-width: 0;
  }
  .dops .type-toggle button {
    font-size: 11px;
    padding: 0 9px;
    border: 1px solid #e0dbd4;
    background: #faf9f7;
    color: #9a9087;
    cursor: pointer;
    height: 31px;
  }
  .dops .type-toggle button:last-child {
    border-radius: 0 7px 7px 0;
  }
  .dops .type-toggle button.on {
    background: #1a1a1a;
    color: #faf9f7;
    border-color: #1a1a1a;
  }
  .dops .connector {
    font-size: 10px;
    color: #9a9087;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 3px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .dops .connector::before,
  .dops .connector::after {
    content: '';
    flex: 1;
    border-top: 1px dashed #e0dbd4;
  }
  .dops .filter-block {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    padding: 10px 13px;
    margin-bottom: 8px;
    font-size: 12px;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .dops .filter-block select {
    font-size: 12px;
    padding: 4px 7px;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    background: #faf9f7;
    color: #1a1a1a;
    outline: none;
  }
  .dops .filter-block .remove-btn {
    margin-left: auto;
  }
  .dops .alongside-tag {
    font-size: 11px;
    padding: 3px 8px;
    background: #f3f0eb;
    border: 1px solid #e0dbd4;
    border-radius: 20px;
    color: #1a1a1a;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
  .dops .config-section {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 10px;
  }
  .dops .config-header {
    padding: 11px 15px;
    border-bottom: 1px solid #e8e4df;
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9a9087;
  }
  .dops .config-inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .dops .config-left {
    padding: 13px 15px;
    border-right: 1px solid #e8e4df;
  }
  .dops .config-right {
    padding: 13px 15px;
    background: #faf9f7;
  }
  .dops .cfg-label {
    font-size: 11px;
    color: #9a9087;
    display: block;
    margin-bottom: 7px;
    letter-spacing: 0.03em;
  }
  .dops .popup-preview {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    padding: 13px;
  }
  .dops .popup-icon {
    width: 26px;
    height: 26px;
    background: #fff3e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    margin-bottom: 7px;
  }
  .dops .popup-heading {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 3px;
  }
  .dops .popup-body-txt {
    font-size: 11px;
    color: #6b6560;
    line-height: 1.5;
    margin-bottom: 9px;
  }
  .dops .popup-cta-btn {
    font-size: 11px;
    color: #fff;
    background: #1a1a1a;
    border: none;
    border-radius: 6px;
    padding: 5px 11px;
    cursor: pointer;
  }
  .dops .email-preview {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    overflow: hidden;
  }
  .dops .email-topbar {
    background: #1a1a1a;
    padding: 9px 13px;
    color: #faf9f7;
    font-size: 10px;
    letter-spacing: 0.05em;
  }
  .dops .email-bd {
    padding: 13px;
  }
  .dops .email-from {
    font-size: 10px;
    color: #9a9087;
    margin-bottom: 5px;
  }
  .dops .email-subject {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 7px;
  }
  .dops .email-text {
    font-size: 11px;
    color: #6b6560;
    line-height: 1.5;
    margin-bottom: 7px;
  }
  .dops .email-items {
    margin-bottom: 8px;
    min-height: 16px;
  }
  .dops .email-item {
    font-size: 11px;
    color: #1a1a1a;
    padding: 4px 0;
    border-bottom: 1px solid #f0ece7;
    display: flex;
    gap: 5px;
  }
  .dops .email-item:last-child {
    border-bottom: none;
  }
  .dops .email-dot {
    color: #9a9087;
    font-size: 13px;
    line-height: 1.3;
    flex-shrink: 0;
  }
  .dops .email-empty {
    font-size: 11px;
    color: #c5bfb8;
    font-style: italic;
    padding: 3px 0;
  }
  .dops .email-foot {
    font-size: 10px;
    color: #c5bfb8;
    text-align: center;
    padding-top: 7px;
    border-top: 1px solid #f0ece7;
  }
  .dops .rule-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 18px;
    border-top: 1px solid #e8e4df;
    margin-top: 6px;
  }
  /* triggered */
  .dops .trig-page {
    padding: 24px 28px;
  }
  .dops .trig-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .dops .trig-header h2 {
    font-family: 'Georgia', serif;
    font-size: 18px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 3px;
  }
  .dops .trig-header p {
    font-size: 12px;
    color: #9a9087;
  }
  .dops .stat-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }
  .dops .stat-card {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    padding: 14px 16px;
  }
  .dops .stat-lbl {
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9a9087;
    margin-bottom: 6px;
  }
  .dops .stat-val {
    font-size: 22px;
    font-weight: 400;
    font-family: 'Georgia', serif;
    color: #1a1a1a;
    margin-bottom: 2px;
  }
  .dops .stat-sub {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .stat-sub.up {
    color: #a33;
  }
  .dops .stat-sub.dn {
    color: #4a7a3e;
  }
  .dops .toolbar {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }
  .dops .t-search {
    font-size: 12px;
    padding: 8px 12px;
    border: 1px solid #e0dbd4;
    border-radius: 8px;
    background: #fff;
    color: #1a1a1a;
    width: 210px;
    outline: none;
  }
  .dops .t-flt {
    font-size: 12px;
    padding: 8px 10px;
    border: 1px solid #e0dbd4;
    border-radius: 8px;
    background: #fff;
    color: #1a1a1a;
    outline: none;
  }
  .dops .trig-table {
    background: #fff;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    overflow: hidden;
  }
  .dops .trig-table table {
    width: 100%;
    border-collapse: collapse;
  }
  .dops .trig-table thead tr {
    border-bottom: 1px solid #e8e4df;
    background: #faf9f7;
  }
  .dops .trig-table th {
    padding: 10px 12px;
    text-align: left;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9a9087;
    font-weight: 400;
    white-space: nowrap;
  }
  .dops .tr-row {
    border-bottom: 1px solid #f0ece7;
    cursor: pointer;
    transition: background 0.1s;
  }
  .dops .tr-row:last-child {
    border-bottom: none;
  }
  .dops .tr-row:hover {
    background: #f5f3ef;
  }
  .dops .tr-row td {
    padding: 11px 12px;
    vertical-align: middle;
  }
  .dops .uid {
    font-weight: 500;
    color: #1a1a1a;
    font-size: 12px;
  }
  .dops .ts {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .rule-tag {
    font-size: 11px;
    padding: 3px 9px;
    border-radius: 20px;
    background: #f3f0eb;
    color: #1a1a1a;
    display: inline-block;
  }
  .dops .hist-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .dops .hist-dots {
    display: flex;
    gap: 3px;
  }
  .dops .hist-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #e0dbd4;
    flex-shrink: 0;
  }
  .dops .hist-dot.hit {
    background: #1a1a1a;
  }
  .dops .s-pill {
    font-size: 10px;
    padding: 3px 9px;
    border-radius: 20px;
    display: inline-block;
  }
  .dops .s-blocked {
    background: #fdecea;
    color: #a33;
  }
  .dops .s-resolved {
    background: #e8f2e5;
    color: #4a7a3e;
  }
  .dops .s-active {
    background: #fff3e0;
    color: #a06000;
  }
  .dops .s-dryrun {
    background: #ede9e3;
    color: #5f5e5a;
  }
  .dops .e-auto {
    background: #fdecea;
    color: #a33;
  }
  .dops .e-manual {
    background: #e8f2e5;
    color: #4a7a3e;
  }
  .dops .e-none {
    background: #f3f0eb;
    color: #9a9087;
  }
  .dops .act-cell {
    display: flex;
    gap: 6px;
  }
  .dops .act-btn {
    font-size: 11px;
    padding: 5px 10px;
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
  }
  .dops .act-review {
    background: none;
    border: 1px solid #e0dbd4;
    color: #1a1a1a;
  }
  .dops .act-review:hover {
    border-color: #1a1a1a;
  }
  .dops .act-escalate {
    background: #1a1a1a;
    border: 1px solid #1a1a1a;
    color: #faf9f7;
  }
  .dops .act-escalate:hover {
    background: #333;
  }
  /* profiles */
  .dops .profile-layout {
    display: flex;
    min-height: 660px;
  }
  .dops .profile-sidebar {
    width: 220px;
    min-width: 220px;
    background: #f3f0eb;
    border-right: 1px solid #e8e4df;
    padding: 24px 0;
  }
  .dops .ps-top {
    padding: 0 20px 20px;
    border-bottom: 1px solid #e8e4df;
    margin-bottom: 8px;
  }
  .dops .ps-top h2 {
    font-size: 14px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 4px;
    font-family: 'Georgia', serif;
  }
  .dops .ps-top p {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .profile-item {
    padding: 12px 20px;
    cursor: pointer;
    border-left: 2px solid transparent;
    white-space: nowrap;
  }
  .dops .profile-item.active {
    background: #fff;
    border-left-color: #1a1a1a;
  }
  .dops .profile-item:hover:not(.active) {
    background: #ede9e3;
  }
  .dops .p-name {
    font-size: 13px;
    color: #1a1a1a;
    margin-bottom: 2px;
  }
  .dops .p-meta {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .profile-main {
    flex: 1;
    padding: 28px 32px;
    overflow-y: auto;
    min-width: 0;
  }
  .dops .profile-title {
    font-size: 20px;
    font-weight: 400;
    font-family: 'Georgia', serif;
    color: #1a1a1a;
    margin-bottom: 4px;
  }
  .dops .profile-sub {
    font-size: 12px;
    color: #9a9087;
    margin-bottom: 24px;
  }
  .dops .p-actions-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 4px;
  }
  .dops .p-ac {
    padding: 11px 13px;
    border: 1px solid #e0dbd4;
    border-radius: 10px;
    cursor: pointer;
    background: #fff;
    transition:
      border-color 0.1s,
      background 0.1s;
  }
  .dops .p-ac:hover {
    border-color: #1a1a1a;
  }
  .dops .p-ac.on {
    border-color: #1a1a1a;
    background: #f5f3ef;
  }
  .dops .p-ac-name {
    font-size: 12px;
    font-weight: 500;
    color: #1a1a1a;
    margin-bottom: 2px;
  }
  .dops .p-ac-desc {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .popup-type-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
  }
  .dops .popup-type-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border: 1px solid #e0dbd4;
    border-radius: 8px;
    cursor: pointer;
    background: #fff;
  }
  .dops .popup-type-item:hover {
    border-color: #1a1a1a;
  }
  .dops .popup-type-item.selected {
    border-color: #1a1a1a;
    background: #f5f3ef;
  }
  .dops .popup-type-item input[type='radio'] {
    accent-color: #1a1a1a;
    flex-shrink: 0;
  }
  .dops .popup-type-label {
    font-size: 12px;
    color: #1a1a1a;
  }
  .dops .multi-select {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .dops .multi-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 7px 10px;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    background: #fff;
    cursor: pointer;
  }
  .dops .multi-item:hover {
    border-color: #1a1a1a;
  }
  .dops .multi-item.checked {
    border-color: #1a1a1a;
    background: #f5f3ef;
  }
  .dops .multi-item input[type='checkbox'] {
    accent-color: #1a1a1a;
    width: 13px;
    height: 13px;
    margin-top: 1px;
    flex-shrink: 0;
    pointer-events: none;
  }
  .dops .multi-item span {
    font-size: 11px;
    color: #1a1a1a;
    line-height: 1.4;
  }
  /* drawer */
  .dops-drawer-bd {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 50;
  }
  .dops-drawer-bd.show {
    display: block;
  }
  .dops-drawer {
    position: fixed;
    top: 0;
    right: -440px;
    width: 420px;
    height: 100vh;
    background: #fff;
    border-left: 1px solid #e0dbd4;
    z-index: 51;
    transition: right 0.25s;
    overflow-y: auto;
    padding: 28px;
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
    font-size: 13px;
    color: #1a1a1a;
  }
  .dops-drawer.show {
    right: 0;
  }
  .dops .d-hdr {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
  }
  .dops .d-title {
    font-family: 'Georgia', serif;
    font-size: 18px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 3px;
  }
  .dops .d-sub {
    font-size: 11px;
    color: #9a9087;
  }
  .dops .d-close {
    background: none;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    color: #9a9087;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dops .d-close:hover {
    border-color: #1a1a1a;
    color: #1a1a1a;
  }
  .dops-drawer .d-hdr {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
  }
  .dops-drawer .d-title {
    font-family: 'Georgia', serif;
    font-size: 18px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 3px;
  }
  .dops-drawer .d-sub {
    font-size: 11px;
    color: #9a9087;
  }
  .dops-drawer .d-close {
    background: none;
    border: 1px solid #e0dbd4;
    border-radius: 7px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    color: #9a9087;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dops-drawer .d-close:hover {
    border-color: #1a1a1a;
    color: #1a1a1a;
  }
  .dops-drawer .d-sec {
    margin-bottom: 18px;
  }
  .dops-drawer .d-sec-lbl {
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9a9087;
    margin-bottom: 10px;
  }
  .dops-drawer .d-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #f0ece7;
    font-size: 12px;
  }
  .dops-drawer .d-row:last-child {
    border-bottom: none;
  }
  .dops-drawer .d-key {
    color: #9a9087;
  }
  .dops-drawer .d-val {
    color: #1a1a1a;
    font-weight: 500;
  }
  .dops-drawer .d-divider {
    border: none;
    border-top: 1px solid #e8e4df;
    margin: 14px 0;
  }
  .dops-drawer .timeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .dops-drawer .tl-item {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }
  .dops-drawer .tl-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1a1a1a;
    flex-shrink: 0;
    margin-top: 4px;
  }
  .dops-drawer .tl-dot.old {
    background: #e0dbd4;
  }
  .dops-drawer .tl-text {
    font-size: 12px;
    color: #1a1a1a;
    line-height: 1.4;
  }
  .dops-drawer .tl-time {
    font-size: 11px;
    color: #9a9087;
  }
  .dops-drawer .d-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid #e8e4df;
  }
  .dops-drawer .d-btn {
    width: 100%;
    padding: 10px;
    border-radius: 8px;
    font-size: 12px;
    cursor: pointer;
    letter-spacing: 0.02em;
  }
  .dops-drawer .d-btn-solid {
    background: #1a1a1a;
    border: 1px solid #1a1a1a;
    color: #faf9f7;
  }
  .dops-drawer .d-btn-solid:hover:not(:disabled) {
    background: #333;
  }
  .dops-drawer .d-btn-solid:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .dops-drawer .d-btn-outline {
    background: none;
    border: 1px solid #1a1a1a;
    color: #1a1a1a;
  }
  .dops-drawer .d-btn-outline:hover {
    background: #f5f3ef;
  }
  .dops-drawer .d-btn-ghost {
    background: none;
    border: 1px solid #e0dbd4;
    color: #1a1a1a;
  }
  .dops-drawer .d-btn-ghost:hover {
    background: #f5f3ef;
  }
  .dops-drawer .s-pill {
    font-size: 10px;
    padding: 3px 9px;
    border-radius: 20px;
    display: inline-block;
  }
  .dops-drawer .s-blocked {
    background: #fdecea;
    color: #a33;
  }
  .dops-drawer .s-resolved {
    background: #e8f2e5;
    color: #4a7a3e;
  }
  .dops-drawer .s-active {
    background: #fff3e0;
    color: #a06000;
  }
  .dops-drawer .s-dryrun {
    background: #ede9e3;
    color: #5f5e5a;
  }
  .dops-drawer .e-auto {
    background: #fdecea;
    color: #a33;
  }
  .dops-drawer .e-manual {
    background: #e8f2e5;
    color: #4a7a3e;
  }
  .dops-drawer .e-none {
    background: #f3f0eb;
    color: #9a9087;
  }
  /* modal */
  .dops-modal-bd {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 100;
    align-items: center;
    justify-content: center;
  }
  .dops-modal-bd.show {
    display: flex;
  }
  .dops-modal {
    background: #faf9f7;
    border-radius: 14px;
    padding: 26px;
    width: 330px;
    border: 1px solid #e0dbd4;
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
    font-size: 13px;
    color: #1a1a1a;
  }
  .dops-modal h3 {
    font-size: 16px;
    font-weight: 400;
    font-family: 'Georgia', serif;
    margin-bottom: 5px;
    color: #1a1a1a;
  }
  .dops-modal p {
    font-size: 12px;
    color: #9a9087;
    margin-bottom: 16px;
  }
  .dops-modal label {
    font-size: 11px;
    color: #9a9087;
    display: block;
    margin-bottom: 4px;
  }
  .dops-modal input,
  .dops-modal select {
    font-size: 12px;
    padding: 8px 11px;
    border: 1px solid #e0dbd4;
    border-radius: 8px;
    background: #fff;
    color: #1a1a1a;
    width: 100%;
    outline: none;
    margin-bottom: 10px;
    box-sizing: border-box;
  }
  .dops-modal .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .dops-modal .btn {
    font-size: 12px;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    letter-spacing: 0.02em;
  }
  .dops-modal .btn-ghost {
    background: none;
    border: 1px solid #e0dbd4;
    color: #1a1a1a;
  }
  .dops-modal .btn-solid {
    background: #1a1a1a;
    border: 1px solid #1a1a1a;
    color: #faf9f7;
  }
  /* toast */
  .dops-toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: #1a1a1a;
    color: #faf9f7;
    font-size: 12px;
    padding: 10px 20px;
    border-radius: 8px;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
    z-index: 999;
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
  }
  .dops-toast.show {
    opacity: 1;
  }
`;

// ── Small helpers ──────────────────────────────────────────────────────────
function StatusPill({ status }: { status: StatusType }) {
  const map: Record<StatusType, [string, string]> = {
    blocked: ['s-blocked', 'Blocked'],
    resolved: ['s-resolved', 'Resolved'],
    active: ['s-active', 'Active'],
    dryrun: ['s-dryrun', 'Dry run'],
  };
  const [cls, label] = map[status];
  return <span className={`s-pill ${cls}`}>{label}</span>;
}
function EscPill({ esc }: { esc: EscType }) {
  if (esc === 'auto') return <span className="s-pill e-auto">Auto-escalated</span>;
  if (esc === 'manual') return <span className="s-pill e-manual">Manual</span>;
  return <span className="s-pill e-none">—</span>;
}
function EmailPreview({ keys }: { keys: string[] }) {
  if (!keys.length) return <div className="email-empty">No action items selected.</div>;
  return (
    <>
      {keys.map(k => {
        const item = EMAIL_ITEMS.find(e => e.key === k);
        return item ? (
          <div key={k} className="email-item">
            <span className="email-dot">·</span>
            <span>{item.text}</span>
          </div>
        ) : null;
      })}
    </>
  );
}
function getPopupType(key: string) {
  return POPUP_TYPES.find(p => p.key === key) || POPUP_TYPES[0];
}

// ── Main component ─────────────────────────────────────────────────────────
export default function DeliverabilityOps() {
  const [activeTab, setActiveTab] = useState<TabId>('rules');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState('r1');
  const [rules, setRules] = useState<Rule[]>(INIT_RULES);
  const [conditions, setConditions] = useState<Condition[]>([
    {
      id: 'c1',
      signal: 'Bounces',
      window: '7 days',
      operator: '>',
      threshold: '5',
      unit: '%',
      bounceReason: '',
    },
    {
      id: 'c2',
      signal: 'Opens',
      window: '30 days',
      operator: '<',
      threshold: '20',
      unit: '%',
      bounceReason: '',
    },
  ]);
  const [filters, setFilters] = useState<FilterItem[]>([
    { id: 'f1', type: 'status', s1: 'Activating', s2: 'Suspended' },
    { id: 'f2', type: 'cooldown', days: '30 days' },
    {
      id: 'f3',
      type: 'alongside',
      alRules: ['High complaint rate'],
      scope: 'this rule',
      days: '30',
    },
  ]);
  const [ruleDesc, setRuleDesc] = useState(
    "Triggers when a sender's hard bounce rate exceeds threshold over a 7-day rolling window.",
  );
  const [profileSelect, setProfileSelect] = useState('p1');
  const [profiles, setProfiles] = useState<Record<string, Profile>>({ ...INIT_PROFILES });
  const [currentProfileId, setCurrentProfileId] = useState('p1');
  const [profileEditor, setProfileEditor] = useState<Profile>(() => ({
    ...INIT_PROFILES.p1,
    actions: [...INIT_PROFILES.p1.actions],
    emailKeys: [...INIT_PROFILES.p1.emailKeys],
  }));
  const [trigRows, setTrigRows] = useState<TrigRow[]>(TRIG_DATA_INIT);
  const [search, setSearch] = useState('');
  const [fltRule, setFltRule] = useState('all');
  const [fltStatus, setFltStatus] = useState('all');
  const [fltEsc, setFltEsc] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerIdx, setDrawerIdx] = useState<number | null>(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [modalName, setModalName] = useState('');
  const [modalStatus, setModalStatus] = useState('Active');
  const [npModalOpen, setNpModalOpen] = useState(false);
  const [npName, setNpName] = useState('');
  const [npDesc, setNpDesc] = useState('');
  const [toast, setToast] = useState('');
  const [toastShow, setToastShow] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  const showToast = (msg: string) => {
    setToast(msg);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 2200);
  };

  const selectedRule = rules.find(r => r.id === selectedRuleId) || rules[0];

  const filteredRows = useMemo(
    () =>
      trigRows.filter(r => {
        if (
          search &&
          !r.uid.toLowerCase().includes(search.toLowerCase()) &&
          !r.rule.toLowerCase().includes(search.toLowerCase())
        )
          return false;
        if (fltRule !== 'all' && r.rule !== fltRule) return false;
        if (fltStatus !== 'all' && r.status !== fltStatus) return false;
        if (fltEsc !== 'all') {
          if (fltEsc === 'auto' && r.escalation !== 'auto') return false;
          if (fltEsc === 'manual' && r.escalation !== 'manual') return false;
          if (fltEsc === 'none' && r.escalation !== null) return false;
        }
        return true;
      }),
    [trigRows, search, fltRule, fltStatus, fltEsc],
  );

  const drawerRow = drawerIdx !== null ? trigRows[drawerIdx] : null;

  // Sync profile editor when switching profiles
  const switchProfile = (pid: string) => {
    setCurrentProfileId(pid);
    const p = profiles[pid];
    setProfileEditor({ ...p, actions: [...p.actions], emailKeys: [...p.emailKeys] });
  };

  const ruleTabProfile = profiles[profileSelect];
  const rulePopupType = getPopupType(ruleTabProfile?.popupKey || 'bounce');

  // ── Condition handlers ────────────────────────────────────────────
  const addCondition = () =>
    setConditions(prev => [
      ...prev,
      {
        id: genId(),
        signal: 'Bounces',
        window: '7 days',
        operator: '>',
        threshold: '0',
        unit: '%',
        bounceReason: '',
      },
    ]);
  const removeCondition = (id: string) => setConditions(prev => prev.filter(c => c.id !== id));
  const updateCond = (id: string, field: keyof Condition, value: string) =>
    setConditions(prev => prev.map(c => (c.id === id ? { ...c, [field]: value } : c)));

  // ── Filter handlers ───────────────────────────────────────────────
  const addFilter = () =>
    setFilters(prev => [
      ...prev,
      { id: genId(), type: 'status', s1: 'Activating', s2: 'Suspended' },
    ]);
  const removeFilter = (id: string) => setFilters(prev => prev.filter(f => f.id !== id));
  const addAlongsideRule = (fid: string, ruleName: string) => {
    if (!ruleName) return;
    setFilters(prev =>
      prev.map(f =>
        f.id === fid && f.type === 'alongside'
          ? { ...f, alRules: f.alRules.includes(ruleName) ? f.alRules : [...f.alRules, ruleName] }
          : f,
      ),
    );
  };
  const removeAlongsideRule = (fid: string, ruleName: string) =>
    setFilters(prev =>
      prev.map(f =>
        f.id === fid && f.type === 'alongside'
          ? { ...f, alRules: f.alRules.filter(r => r !== ruleName) }
          : f,
      ),
    );

  // ── Rule modal ────────────────────────────────────────────────────
  const createRule = () => {
    if (!modalName.trim()) return;
    const newRule: Rule = {
      id: genId(),
      name: modalName.trim(),
      meta: 'Never triggered · 0 total triggers',
      active: modalStatus === 'Active',
      status: modalStatus,
      trigCount: 0,
    };
    setRules(prev => [...prev, newRule]);
    setSelectedRuleId(newRule.id);
    setRuleModalOpen(false);
    setModalName('');
    setModalStatus('Active');
    showToast(`"${newRule.name}" created`);
  };

  // ── Profile handlers ──────────────────────────────────────────────
  const saveProfile = () => {
    setProfiles(prev => ({ ...prev, [currentProfileId]: { ...profileEditor } }));
    showToast(`${profileEditor.name} saved`);
  };
  const deleteProfile = () => {
    const keys = Object.keys(profiles);
    if (keys.length <= 1) {
      showToast('Cannot delete the last profile');
      return;
    }
    const next = { ...profiles };
    delete next[currentProfileId];
    setProfiles(next);
    const firstId = Object.keys(next)[0];
    switchProfile(firstId);
    showToast('Profile deleted');
  };
  const createProfile = () => {
    if (!npName.trim()) return;
    const pid = `p_${genId()}`;
    const np: Profile = {
      name: npName.trim(),
      desc: npDesc.trim() || 'Custom profile',
      actions: ['popup', 'email'],
      popupKey: 'bounce',
      emailKeys: ['remove'],
    };
    setProfiles(prev => ({ ...prev, [pid]: np }));
    switchProfile(pid);
    setNpModalOpen(false);
    setNpName('');
    setNpDesc('');
    showToast(`"${np.name}" created`);
  };
  const toggleProfileAction = (key: string) => {
    setProfileEditor(prev => ({
      ...prev,
      actions: prev.actions.includes(key)
        ? prev.actions.filter(a => a !== key)
        : [...prev.actions, key],
    }));
  };
  const toggleEmailKey = (key: string) => {
    setProfileEditor(prev => ({
      ...prev,
      emailKeys: prev.emailKeys.includes(key)
        ? prev.emailKeys.filter(k => k !== key)
        : [...prev.emailKeys, key],
    }));
  };

  // ── Drawer / escalation ───────────────────────────────────────────
  const openDrawer = (idx: number) => {
    setDrawerIdx(idx);
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);
  const quickEscalate = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setTrigRows(prev => prev.map((r, i) => (i === idx ? { ...r, escalation: 'manual' } : r)));
    showToast('Escalated to TnS');
  };
  const manualEscalate = () => {
    if (drawerIdx === null) return;
    setTrigRows(prev => prev.map((r, i) => (i === drawerIdx ? { ...r, escalation: 'manual' } : r)));
    showToast('Escalated to TnS with full context');
    closeDrawer();
  };

  // ── Render helpers ────────────────────────────────────────────────
  const renderCondition = (cond: Condition, idx: number) => (
    <div key={cond.id}>
      {idx > 0 && <div className="connector">and</div>}
      <div className="logic-card">
        <div className="logic-row-top">
          <div>
            <div className="logic-lbl">Signal</div>
            <select
              className="logic-select"
              value={cond.signal}
              onChange={e => updateCond(cond.id, 'signal', e.target.value)}
            >
              {['Bounces', 'Opens', 'Deliveries', 'Unsubscribes', 'Blocklist'].map(s => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="logic-lbl">Window</div>
            <select
              className="logic-select"
              value={cond.window}
              onChange={e => updateCond(cond.id, 'window', e.target.value)}
            >
              {['7 days', '14 days', '30 days'].map(w => (
                <option key={w}>{w}</option>
              ))}
            </select>
          </div>
        </div>
        {cond.signal === 'Bounces' && (
          <div style={{ margin: '10px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div className="logic-lbl">Bounce reason</div>
            <select
              className="logic-select"
              style={{ width: '100%' }}
              value={cond.bounceReason}
              onChange={e => updateCond(cond.id, 'bounceReason', e.target.value)}
            >
              <option value="">Any reason</option>
              {[
                'Invalid recipient',
                'Domain low reputation',
                'Mailbox rate limited',
                'Recipient domain DNS failure',
                'Mailbox full',
              ].map(r => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
        )}
        <div className="logic-row-bottom">
          <div>
            <div className="logic-lbl">Operator</div>
            <select
              className="logic-select"
              value={cond.operator}
              onChange={e => updateCond(cond.id, 'operator', e.target.value)}
            >
              {['>', '<', '>=', '<=', '=='].map(op => (
                <option key={op}>{op}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="logic-lbl">Threshold</div>
            <div className="val-wrap">
              <input
                className="logic-input"
                type="text"
                value={cond.threshold}
                onChange={e => updateCond(cond.id, 'threshold', e.target.value)}
              />
              <div className="type-toggle">
                <button
                  className={cond.unit === '%' ? 'on' : ''}
                  onClick={() => updateCond(cond.id, 'unit', '%')}
                >
                  %
                </button>
                <button
                  className={cond.unit === '#' ? 'on' : ''}
                  onClick={() => updateCond(cond.id, 'unit', '#')}
                >
                  #
                </button>
              </div>
            </div>
          </div>
          <button className="remove-btn" onClick={() => removeCondition(cond.id)}>
            ×
          </button>
        </div>
      </div>
    </div>
  );

  const renderFilter = (f: FilterItem) => {
    if (f.type === 'status')
      return (
        <div key={f.id} className="filter-block">
          Skip if sender status is
          <select
            value={f.s1}
            onChange={e =>
              setFilters(prev =>
                prev.map(x => (x.id === f.id ? ({ ...x, s1: e.target.value } as FilterItem) : x)),
              )
            }
          >
            {['Activating', 'Suspended'].map(s => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <span style={{ color: '#9a9087' }}>or</span>
          <select
            value={f.s2}
            onChange={e =>
              setFilters(prev =>
                prev.map(x => (x.id === f.id ? ({ ...x, s2: e.target.value } as FilterItem) : x)),
              )
            }
          >
            {['Suspended', 'Activating'].map(s => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <button className="remove-btn" onClick={() => removeFilter(f.id)}>
            ×
          </button>
        </div>
      );
    if (f.type === 'cooldown')
      return (
        <div key={f.id} className="filter-block">
          Cooldown — cannot re-trigger within
          <select
            value={f.days}
            onChange={e =>
              setFilters(prev =>
                prev.map(x => (x.id === f.id ? ({ ...x, days: e.target.value } as FilterItem) : x)),
              )
            }
          >
            {['30 days', '7 days', '14 days', '60 days'].map(d => (
              <option key={d}>{d}</option>
            ))}
          </select>
          after last trigger
          <button className="remove-btn" onClick={() => removeFilter(f.id)}>
            ×
          </button>
        </div>
      );
    if (f.type === 'alongside')
      return (
        <div key={f.id} className="filter-block">
          If triggered alongside
          <div
            style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}
          >
            {f.alRules.map(r => (
              <span key={r} className="alongside-tag">
                {r}
                <button
                  onClick={() => removeAlongsideRule(f.id, r)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9a9087',
                    fontSize: '12px',
                    padding: '0 0 0 3px',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            <select
              onChange={e => {
                addAlongsideRule(f.id, e.target.value);
                e.target.value = '';
              }}
              style={{
                fontSize: '12px',
                padding: '4px 7px',
                border: '1px solid #e0dbd4',
                borderRadius: '7px',
                background: '#faf9f7',
                color: '#9a9087',
                outline: 'none',
              }}
            >
              <option value="">+ Add rule</option>
              {[
                'Low open rate',
                'Blocklist detected',
                'High bounce rate',
                'High complaint rate',
              ].map(r => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          count as a single trigger for
          <select
            value={f.scope}
            onChange={e =>
              setFilters(prev =>
                prev.map(x =>
                  x.id === f.id ? ({ ...x, scope: e.target.value } as FilterItem) : x,
                ),
              )
            }
            style={{
              fontSize: '12px',
              padding: '4px 7px',
              border: '1px solid #e0dbd4',
              borderRadius: '7px',
              background: '#faf9f7',
              color: '#1a1a1a',
              outline: 'none',
            }}
          >
            <option>this rule</option>
            <option>both rules</option>
          </select>
          within
          <input
            type="number"
            value={f.days}
            min={1}
            onChange={e =>
              setFilters(prev =>
                prev.map(x => (x.id === f.id ? ({ ...x, days: e.target.value } as FilterItem) : x)),
              )
            }
            style={{
              width: '50px',
              padding: '3px 6px',
              fontSize: '12px',
              border: '1px solid #e0dbd4',
              borderRadius: '7px',
              background: '#faf9f7',
              color: '#1a1a1a',
              outline: 'none',
            }}
          />
          days
          <button className="remove-btn" onClick={() => removeFilter(f.id)}>
            ×
          </button>
        </div>
      );
    return null;
  };

  return (
    <div className="dops">
      <Global styles={globalStyles} />

      {/* ── Nav ── */}
      <div className="app">
        <div className="nav">
          <div className="nav-logo">Deliverability ops</div>
          <div className="nav-tabs">
            <button
              className={`nav-tab${activeTab === 'rules' ? ' active' : ''}`}
              onClick={() => setActiveTab('rules')}
            >
              Rules
            </button>
            <button
              className={`nav-tab${activeTab === 'profiles' ? ' active' : ''}`}
              onClick={() => setActiveTab('profiles')}
            >
              Profile settings
            </button>
            <button
              className={`nav-tab${activeTab === 'triggered' ? ' active' : ''}`}
              onClick={() => setActiveTab('triggered')}
            >
              Users triggered <span className="tab-count">{trigRows.length}</span>
            </button>
          </div>
        </div>

        {/* ── Rules Tab ── */}
        {activeTab === 'rules' && (
          <div className="rules-layout">
            <div className={`sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
              <div className="sb-top">
                <h2>Rules</h2>
                <button className="new-btn" onClick={() => setRuleModalOpen(true)}>
                  + Create rule
                </button>
              </div>
              {rules.map(r => (
                <div
                  key={r.id}
                  className={`rule-item${selectedRuleId === r.id ? ' active' : ''}`}
                  onClick={() => setSelectedRuleId(r.id)}
                >
                  <div className="r-name">{r.name}</div>
                  <div className="r-meta">
                    <span className={`dot ${r.active ? 'dot-on' : 'dot-off'}`} />
                    <span className={`pill ${r.active ? 'pill-on' : 'pill-off'}`}>{r.status}</span>
                    {r.trigCount} triggers
                  </div>
                </div>
              ))}
            </div>

            <div className="main">
              <div className="rule-topbar">
                <button className="toggle-sb" onClick={() => setSidebarCollapsed(c => !c)}>
                  ☰
                </button>
                <div className="page-title">{selectedRule.name}</div>
              </div>
              <div className="page-sub">{selectedRule.meta}</div>

              {/* Basic info */}
              <div style={{ marginBottom: '22px' }}>
                <div className="sec-label">Basic info</div>
                <div className="row2">
                  <div className="field">
                    <label>Rule name</label>
                    <input type="text" defaultValue={selectedRule.name} />
                  </div>
                  <div className="field">
                    <label>Status</label>
                    <select defaultValue={selectedRule.status}>
                      <option>Active</option>
                      <option>Inactive</option>
                      <option>Dry run</option>
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label>Description</label>
                  <textarea value={ruleDesc} onChange={e => setRuleDesc(e.target.value)} />
                </div>
              </div>
              <hr className="divider" />

              {/* Signal & logic */}
              <div style={{ marginBottom: '22px' }}>
                <div className="sec-label">Signal &amp; logic</div>
                {conditions.map((c, i) => renderCondition(c, i))}
                <button className="add-link" style={{ marginTop: '6px' }} onClick={addCondition}>
                  + Add condition
                </button>
              </div>
              <hr className="divider" />

              {/* Filters */}
              <div style={{ marginBottom: '22px' }}>
                <div className="sec-label">Filters</div>
                {filters.map(f => renderFilter(f))}
                <button className="add-link" style={{ marginTop: '6px' }} onClick={addFilter}>
                  + Add filter
                </button>
              </div>
              <hr className="divider" />

              {/* User profile */}
              <div style={{ marginBottom: '22px' }}>
                <div className="sec-label">User profile</div>
                <div className="field">
                  <label>Select a profile to apply to this rule</label>
                  <select
                    value={profileSelect}
                    onChange={e => setProfileSelect(e.target.value)}
                    style={{
                      maxWidth: '320px',
                      fontSize: '12px',
                      padding: '8px 11px',
                      border: '1px solid #e0dbd4',
                      borderRadius: '8px',
                      background: '#fff',
                      color: '#1a1a1a',
                      outline: 'none',
                    }}
                  >
                    {Object.entries(profiles).map(([pid, p]) => (
                      <option key={pid} value={pid}>
                        {p.name} — {p.desc}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-section">
                  <div className="config-header">
                    Profile actions{' '}
                    <span
                      style={{
                        fontSize: '10px',
                        color: '#c5bfb8',
                        marginLeft: '6px',
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                    >
                      — read only ·{' '}
                      <span
                        style={{ color: '#1a1a1a', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setActiveTab('profiles')}
                      >
                        edit in Profile settings
                      </span>
                    </span>
                  </div>
                  <div style={{ padding: '14px 16px' }}>
                    <div className="sec-label" style={{ marginBottom: '8px' }}>
                      Actions
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        marginBottom: '16px',
                      }}
                    >
                      {ruleTabProfile?.actions.map(k => {
                        const a = ALL_ACTIONS.find(x => x.key === k);
                        return a ? (
                          <span
                            key={k}
                            style={{
                              fontSize: '11px',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              background: '#f3f0eb',
                              color: '#1a1a1a',
                              border: '1px solid #e0dbd4',
                            }}
                          >
                            {a.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <div className="sec-label" style={{ marginBottom: '8px' }}>
                          In-app popup
                        </div>
                        <div className="popup-preview">
                          <div className="popup-icon">⚠</div>
                          <div className="popup-heading">{rulePopupType.heading}</div>
                          <div className="popup-body-txt">{rulePopupType.body}</div>
                          <button className="popup-cta-btn">View recommendations</button>
                        </div>
                      </div>
                      <div>
                        <div className="sec-label" style={{ marginBottom: '8px' }}>
                          Automated email action items
                        </div>
                        <div className="email-preview">
                          <div className="email-topbar">Deliverability alert</div>
                          <div className="email-bd">
                            <div className="email-from">
                              From: Deliverability Team &lt;deliverability@flodesk.com&gt;
                            </div>
                            <div className="email-subject">
                              Action required: your account needs attention
                            </div>
                            <div className="email-text">Hi there, please take these steps:</div>
                            <div className="email-items">
                              <EmailPreview keys={ruleTabProfile?.emailKeys || []} />
                            </div>
                            <div className="email-foot">
                              You&apos;re receiving this because a rule was triggered.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rule-footer">
                <button className="btn btn-ghost" onClick={() => showToast('Changes discarded')}>
                  Discard
                </button>
                <button className="btn btn-outline">Preview affected senders</button>
                <button className="btn btn-solid" onClick={() => showToast('Rule saved')}>
                  Save rule
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Triggered Tab ── */}
        {activeTab === 'triggered' && (
          <div className="trig-page">
            <div className="trig-header">
              <div>
                <h2>Users triggered</h2>
                <p>Real-time view of rule triggers across all senders</p>
              </div>
              <select className="t-flt">
                <option>Last 7 days</option>
                <option defaultValue="Last 30 days">Last 30 days</option>
                <option>Last 90 days</option>
              </select>
            </div>
            <div className="stat-row">
              <div className="stat-card">
                <div className="stat-lbl">Total triggers</div>
                <div className="stat-val">38</div>
                <div className="stat-sub up">↑ 12% vs last period</div>
              </div>
              <div className="stat-card">
                <div className="stat-lbl">Unique users</div>
                <div className="stat-val">24</div>
                <div className="stat-sub up">↑ 8% vs last period</div>
              </div>
              <div className="stat-card">
                <div className="stat-lbl">Blocked</div>
                <div className="stat-val">9</div>
                <div className="stat-sub">3 pending review</div>
              </div>
              <div className="stat-card">
                <div className="stat-lbl">TnS escalated</div>
                <div className="stat-val">6</div>
                <div className="stat-sub dn">4 auto · 2 manual</div>
              </div>
            </div>
            <div className="toolbar">
              <input
                className="t-search"
                type="text"
                placeholder="Search by user ID or rule..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select className="t-flt" value={fltRule} onChange={e => setFltRule(e.target.value)}>
                <option value="all">All rules</option>
                {[
                  'High bounce rate',
                  'High complaint rate',
                  'Low open rate',
                  'Blocklist detected',
                ].map(r => (
                  <option key={r}>{r}</option>
                ))}
              </select>
              <select
                className="t-flt"
                value={fltStatus}
                onChange={e => setFltStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="blocked">Blocked</option>
                <option value="active">Active</option>
                <option value="resolved">Resolved</option>
                <option value="dryrun">Dry run</option>
              </select>
              <select className="t-flt" value={fltEsc} onChange={e => setFltEsc(e.target.value)}>
                <option value="all">All escalations</option>
                <option value="auto">Auto-escalated</option>
                <option value="manual">Manual</option>
                <option value="none">Not escalated</option>
              </select>
            </div>
            <div className="trig-table">
              <table>
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Trigger time</th>
                    <th>Rule triggered</th>
                    <th>Historical triggers</th>
                    <th>Status</th>
                    <th>TnS escalation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#9a9087',
                          fontSize: '12px',
                        }}
                      >
                        No results found
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map(r => {
                      const globalIdx = trigRows.indexOf(r);
                      const dots = r.history.slice(-8);
                      return (
                        <tr key={r.uid} className="tr-row" onClick={() => openDrawer(globalIdx)}>
                          <td>
                            <div className="uid">{r.uid}</div>
                          </td>
                          <td>
                            <div className="ts">{r.time}</div>
                          </td>
                          <td>
                            <span className="rule-tag">{r.rule}</span>
                          </td>
                          <td>
                            <div className="hist-wrap">
                              <div className="hist-dots">
                                {dots.map((_, di) => (
                                  <div key={di} className="hist-dot hit" />
                                ))}
                              </div>
                              <span style={{ fontSize: '11px', color: '#9a9087' }}>
                                {r.history.length}×
                              </span>
                            </div>
                          </td>
                          <td>
                            <StatusPill status={r.status} />
                          </td>
                          <td>
                            <EscPill esc={r.escalation} />
                          </td>
                          <td>
                            <div className="act-cell">
                              <button
                                className="act-btn act-review"
                                onClick={e => {
                                  e.stopPropagation();
                                  openDrawer(globalIdx);
                                }}
                              >
                                Review
                              </button>
                              {!r.escalation && (
                                <button
                                  className="act-btn act-escalate"
                                  onClick={e => quickEscalate(globalIdx, e)}
                                >
                                  Escalate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Profiles Tab ── */}
        {activeTab === 'profiles' && (
          <div className="profile-layout">
            <div className="profile-sidebar">
              <div className="ps-top">
                <h2>Profiles</h2>
                <p>Define actions and messaging per profile</p>
              </div>
              {Object.entries(profiles).map(([pid, p]) => (
                <div
                  key={pid}
                  className={`profile-item${currentProfileId === pid ? ' active' : ''}`}
                  onClick={() => switchProfile(pid)}
                >
                  <div className="p-name">{p.name}</div>
                  <div className="p-meta">{p.desc}</div>
                </div>
              ))}
              <div
                style={{ padding: '16px 20px', borderTop: '1px solid #e8e4df', marginTop: '8px' }}
              >
                <button className="new-btn" onClick={() => setNpModalOpen(true)}>
                  + Create profile
                </button>
              </div>
            </div>

            <div className="profile-main">
              <input
                value={profileEditor.name}
                onChange={e => setProfileEditor(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  fontFamily: "'Georgia',serif",
                  fontSize: '20px',
                  fontWeight: 400,
                  color: '#1a1a1a',
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  borderBottom: '1px dashed #e0dbd4',
                  padding: '2px 4px',
                  width: '260px',
                  marginBottom: '4px',
                  display: 'block',
                }}
              />
              <input
                value={profileEditor.desc}
                onChange={e => setProfileEditor(prev => ({ ...prev, desc: e.target.value }))}
                placeholder="Short description..."
                style={{
                  fontSize: '12px',
                  color: '#9a9087',
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  borderBottom: '1px dashed #e0dbd4',
                  padding: '2px 4px',
                  width: '300px',
                  marginBottom: '20px',
                  display: 'block',
                }}
              />

              <div className="sec-label">Actions</div>
              <div className="p-actions-grid">
                {ALL_ACTIONS.map(a => (
                  <div
                    key={a.key}
                    className={`p-ac${profileEditor.actions.includes(a.key) ? ' on' : ''}`}
                    onClick={() => toggleProfileAction(a.key)}
                  >
                    <div className="p-ac-name">{a.name}</div>
                    <div className="p-ac-desc">{a.desc}</div>
                  </div>
                ))}
              </div>
              <hr className="divider" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div className="sec-label" style={{ marginBottom: '10px' }}>
                    In-app popup
                  </div>
                  <div className="popup-type-grid">
                    {POPUP_TYPES.map(pt => (
                      <div
                        key={pt.key}
                        className={`popup-type-item${profileEditor.popupKey === pt.key ? ' selected' : ''}`}
                        onClick={() => setProfileEditor(prev => ({ ...prev, popupKey: pt.key }))}
                      >
                        <input type="radio" readOnly checked={profileEditor.popupKey === pt.key} />
                        <span className="popup-type-label">{pt.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="sec-label" style={{ marginTop: '14px', marginBottom: '8px' }}>
                    Preview
                  </div>
                  <div className="popup-preview">
                    <div className="popup-icon">⚠</div>
                    <div className="popup-heading">
                      {getPopupType(profileEditor.popupKey).heading}
                    </div>
                    <div className="popup-body-txt">
                      {getPopupType(profileEditor.popupKey).body}
                    </div>
                    <button className="popup-cta-btn">View recommendations</button>
                  </div>
                </div>

                <div>
                  <div className="sec-label" style={{ marginBottom: '10px' }}>
                    Automated email — action items
                  </div>
                  <div className="multi-select">
                    {EMAIL_ITEMS.map(ei => (
                      <div
                        key={ei.key}
                        className={`multi-item${profileEditor.emailKeys.includes(ei.key) ? ' checked' : ''}`}
                        onClick={() => toggleEmailKey(ei.key)}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={profileEditor.emailKeys.includes(ei.key)}
                        />
                        <span>{ei.text}</span>
                      </div>
                    ))}
                  </div>
                  <div className="sec-label" style={{ marginTop: '14px', marginBottom: '8px' }}>
                    Email preview
                  </div>
                  <div className="email-preview">
                    <div className="email-topbar">Deliverability alert</div>
                    <div className="email-bd">
                      <div className="email-from">
                        From: Deliverability Team &lt;deliverability@flodesk.com&gt;
                      </div>
                      <div className="email-subject">
                        Action required: your account needs attention
                      </div>
                      <div className="email-text">
                        Hi there, please take these steps to protect your sending reputation:
                      </div>
                      <div className="email-items">
                        <EmailPreview keys={profileEditor.emailKeys} />
                      </div>
                      <div className="email-foot">
                        You&apos;re receiving this because a deliverability rule was triggered.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rule-footer">
                <button
                  className="btn btn-ghost"
                  style={{ color: '#a33', borderColor: '#f5c6c6' }}
                  onClick={deleteProfile}
                >
                  Delete profile
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-ghost" onClick={() => switchProfile(currentProfileId)}>
                    Discard
                  </button>
                  <button className="btn btn-solid" onClick={saveProfile}>
                    Save profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      <div className={`dops-drawer-bd${drawerOpen ? ' show' : ''}`} onClick={closeDrawer} />
      <div className={`dops-drawer${drawerOpen ? ' show' : ''}`}>
        {drawerRow && (
          <>
            <div className="d-hdr">
              <div>
                <div className="d-title">{drawerRow.uid}</div>
                <div className="d-sub">{drawerRow.time}</div>
              </div>
              <button className="d-close" onClick={closeDrawer}>
                ×
              </button>
            </div>
            <div className="d-sec">
              <div className="d-sec-lbl">Trigger details</div>
              <div className="d-row">
                <span className="d-key">Rule</span>
                <span className="d-val">{drawerRow.rule}</span>
              </div>
              <div className="d-row">
                <span className="d-key">Trigger time</span>
                <span className="d-val">{drawerRow.time}</span>
              </div>
              <div className="d-row">
                <span className="d-key">Status</span>
                <span className="d-val">
                  <StatusPill status={drawerRow.status} />
                </span>
              </div>
              <div className="d-row">
                <span className="d-key">TnS escalation</span>
                <span className="d-val">
                  <EscPill esc={drawerRow.escalation} />
                </span>
              </div>
              <div className="d-row">
                <span className="d-key">Total triggers (all time)</span>
                <span className="d-val">{drawerRow.history.length}</span>
              </div>
            </div>
            <hr className="d-divider" />
            <div className="d-sec">
              <div className="d-sec-lbl">Historical triggers</div>
              <div className="timeline">
                {drawerRow.history.map((date, i) => (
                  <div key={i} className="tl-item">
                    <div className={`tl-dot${i > 0 ? ' old' : ''}`} />
                    <div>
                      <div className="tl-text">{drawerRow.rule}</div>
                      <div className="tl-time">{date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="d-footer">
              <button
                className="d-btn d-btn-outline"
                onClick={() => showToast('Opening Admin v2...')}
              >
                Review in Admin v2 ↗
              </button>
              <button
                className="d-btn d-btn-solid"
                disabled={!!drawerRow.escalation}
                onClick={manualEscalate}
              >
                Escalate to TnS with full context
              </button>
              <button className="d-btn d-btn-ghost" onClick={closeDrawer}>
                Close
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Create Rule Modal ── */}
      <div
        className={`dops-modal-bd${ruleModalOpen ? ' show' : ''}`}
        onClick={e => {
          if (e.target === e.currentTarget) setRuleModalOpen(false);
        }}
      >
        <div className="dops-modal">
          <h3>Create new rule</h3>
          <p>Give your rule a name and set an initial status.</p>
          <label>Rule name</label>
          <input
            type="text"
            value={modalName}
            onChange={e => setModalName(e.target.value)}
            placeholder="e.g. High unsubscribe rate"
            onKeyDown={e => e.key === 'Enter' && createRule()}
          />
          <label>Status</label>
          <select value={modalStatus} onChange={e => setModalStatus(e.target.value)}>
            <option>Active</option>
            <option>Inactive</option>
            <option>Dry run</option>
          </select>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setRuleModalOpen(false)}>
              Cancel
            </button>
            <button className="btn btn-solid" onClick={createRule}>
              Create rule
            </button>
          </div>
        </div>
      </div>

      {/* ── Create Profile Modal ── */}
      <div
        className={`dops-modal-bd${npModalOpen ? ' show' : ''}`}
        onClick={e => {
          if (e.target === e.currentTarget) setNpModalOpen(false);
        }}
      >
        <div className="dops-modal">
          <h3>Create new profile</h3>
          <p>Define a name and description for this profile.</p>
          <label>Profile name</label>
          <input
            type="text"
            value={npName}
            onChange={e => setNpName(e.target.value)}
            placeholder="e.g. High risk sender"
          />
          <label>Description</label>
          <input
            type="text"
            value={npDesc}
            onChange={e => setNpDesc(e.target.value)}
            placeholder="e.g. For senders with repeated violations"
            onKeyDown={e => e.key === 'Enter' && createProfile()}
          />
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setNpModalOpen(false)}>
              Cancel
            </button>
            <button className="btn btn-solid" onClick={createProfile}>
              Create
            </button>
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      <div className={`dops-toast${toastShow ? ' show' : ''}`}>{toast}</div>
    </div>
  );
}
